import React from "react";
import { Trans } from "@lingui/macro";
import { Alert } from "components/UI/Alert/Alert";
import { Button } from "components/UI/Button/Button";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "../../../../../UI/Loader/Loader";
import { ISystemState } from "model/system/systemReducer";
import { ClipboardListIcon } from "@heroicons/react/solid";
import { numberCurrencyFormat } from "constants/convertToLocalCurrency";
import { USERPOSITIONS } from "../../../../../../constants/userPositions";
import { getStore } from "../../../../../../model/dashboardStores/dStoresAPI";
import { getUsersList } from "../../../../../../model/dashboardUsers/dUsersAPI";
import { logoutSagaAC } from "../../../../../../model/system/systemActionCreators";
import { dStoresSetDetailItemAC } from "model/dashboardStores/dStoresActionCreators";
import { IdUsersStateDataItem } from "../../../../../../model/dashboardUsers/dUsersReducer";
import {
  UserIcon,
  CashIcon,
  PhoneIcon,
  PencilIcon,
  MinusSmIcon,
  AtSymbolIcon,
  AnnotationIcon,
  LocationMarkerIcon,
} from "@heroicons/react/outline";
import {
  IdStoresState,
  IdStoresStateDataItem,
} from "model/dashboardStores/dStoresReducer";

// Type
export type typePopupDetails = {
  setOpenPopup: React.Dispatch<React.SetStateAction<boolean>>;
};

// Component
export const PopupDetails: React.FC<typePopupDetails> = ({ setOpenPopup }) => {
  const dispatch = useDispatch();
  const currentLang = useSelector(
    (state: { system: ISystemState }) => state.system.language,
  );

  const dataObject = useSelector(
    (state: { dStores: IdStoresState }) => state.dStores.detailItem,
  );

  const [industry, setIndustry] = React.useState<
    IdStoresStateDataItem["industry"] | undefined
  >();

  const [industryLoading, setIndustryLoading] = React.useState<boolean>(true);

  const [loaderManagerList, setLoaderManagerList] = React.useState(true);
  const [managerIsDeleted, setManagerIsDeleted] =
    React.useState<boolean>(false);

  React.useEffect(() => {
    return () => {
      dispatch(dStoresSetDetailItemAC(undefined));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const fetchIndustry = async (id: string) => {
      setIndustryLoading(true);

      try {
        const response = await getStore(id);

        if (response?.data?.industry) {
          setIndustry(response.data.industry);
        }
      } catch (e) {
        console.error(e);
      }

      setIndustryLoading(false);
    };

    if (dataObject?.id) {
      fetchIndustry(dataObject?.id).catch(console.error);
    }
  }, [dataObject?.id]);

  React.useEffect(() => {
    const setData = async () => {
      setLoaderManagerList(true);

      let responseManagers = await getUsersList({
        filters: { deleted: false },
        full: false,
      });

      if (responseManagers.status === 200) {
        const newManagersList = responseManagers.data.filter(
          (item: IdUsersStateDataItem) =>
            item.position === USERPOSITIONS.MANAGER ||
            item.position === USERPOSITIONS.ADMIN,
        );

        const managerId = newManagersList.find(
          (item: IdUsersStateDataItem) => item.name === dataObject!.manager,
        )?.id;

        setManagerIsDeleted(!managerId);
      } else if (responseManagers.status === 401) {
        dispatch(logoutSagaAC());
      }

      setLoaderManagerList(false);
    };

    setData().catch((e) => {
      console.error(e);

      setLoaderManagerList(false);
    });
    // eslint-disable-next-line
  }, []);

  return (
    <>
      {dataObject ? (
        <>
          <div className="grid gap-x-8 grid-cols-1  sm:border sm:rounded-md sm:px-4">
            <div className="flex flex-wrap gap-x-4 py-2 border-b border-t sm:py-4 sm:border-t-0 sm:border-b-0">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <PencilIcon className="h-5 w-5 text-gray-500" />
                <Trans>Store name:</Trans>
              </div>
              <div className="text-gray-90 text-left font-medium break-all">
                {dataObject.name}
              </div>
            </div>
          </div>
          <div className="mt-6 sm:mt-8 grid gap-x-8 grid-cols-1 sm:grid-cols-2 sm:border sm:rounded-md sm:px-4">
            <div className="flex flex-wrap gap-x-4 py-2 border-t border-b sm:py-4 sm:border-t-0">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <LocationMarkerIcon className="h-5 w-5 text-gray-500" />
                <Trans>Address:</Trans>
              </div>
              <div className="flex items-center text-gray-90 text-left break-all">
                {dataObject.address ? (
                  dataObject.address
                ) : (
                  <div className="flex ">
                    <MinusSmIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <PhoneIcon className="h-5 w-5 text-gray-500" />
                <Trans>Store phone:</Trans>
              </div>
              <div className="flex items-center text-gray-90 text-left">
                {dataObject.phoneNumber ? (
                  dataObject.phoneNumber
                ) : (
                  <div className="flex ">
                    <MinusSmIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <AtSymbolIcon className="h-5 w-5 text-gray-500" />
                <Trans>Email:</Trans>
              </div>
              <div className="flex items-center text-gray-90 text-left">
                {dataObject.email ? (
                  dataObject.email
                ) : (
                  <div className="flex ">
                    <MinusSmIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4"></div>
            <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4 sm:border-0 relative">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <UserIcon className="h-5 w-5 text-gray-500" />
                <Trans>Manager:</Trans>
              </div>
              <div className="flex items-center text-gray-90 text-left break-all">
                {loaderManagerList ? (
                  <Loader size={5} />
                ) : dataObject?.manager && !managerIsDeleted ? (
                  dataObject.manager
                ) : (
                  <div className="flex ">
                    <MinusSmIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4 sm:border-0 relative">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <PhoneIcon className="h-5 w-5 text-gray-500" />
                <Trans>Manager phone:</Trans>
              </div>
              <div className="flex items-center text-gray-90 text-left">
                {loaderManagerList ? (
                  <Loader size={5} />
                ) : dataObject?.managerPhoneNumber && !managerIsDeleted ? (
                  dataObject.managerPhoneNumber
                ) : (
                  <div className="flex ">
                    <MinusSmIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="mt-8 grid gap-x-8 grid-cols-1 sm:border sm:rounded-md sm:px-4">
            <div className="flex flex-wrap gap-x-4 py-2 border-b border-t sm:py-4 sm:border-0 relative">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <ClipboardListIcon className="h-5 w-5 text-gray-500" />
                <Trans>Industry:</Trans>
              </div>
              <div className="text-gray-90 text-left">
                {industryLoading ? (
                  <Loader size={5} />
                ) : (
                  industry?.nameMap?.[currentLang] || (
                    <div className="flex ">
                      <MinusSmIcon className="h-5 w-5 text-gray-400" />
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
          {process.env.REACT_APP_ENCASHMENT === "true" && (
            <div className="mt-8 grid gap-x-8 grid-cols-1 sm:border sm:rounded-md sm:px-4">
              <div className="flex flex-wrap gap-x-4 py-2 border-b border-t sm:py-4 sm:border-0">
                <div className="flex gap-x-2  text-gray-500 text-left">
                  <CashIcon className="h-5 w-5 text-gray-500" />
                  <Trans>Cash balance in store:</Trans>
                </div>
                <div className="text-gray-90 ">
                  {dataObject.terminals.length > 0 ? (
                    <div className={"grid grid-cols-3 gap-2 text-gray-500"}>
                      <span>
                        <Trans>terminal's serial number</Trans>
                      </span>
                      <span className={"text-center"}>-</span>{" "}
                      <span className={"text-right"}>
                        <Trans>cash balance</Trans>
                      </span>
                      {/*  @ts-ignore*/}
                      {dataObject.terminals.map((item) => (
                        <React.Fragment key={item.id}>
                          <span className={"text-gray-900 text-left"}>
                            {item.terminalSn}
                          </span>
                          <span className={" text-center"}>-</span>{" "}
                          <span className={"text-gray-900 text-right"}>
                            {numberCurrencyFormat(item.sum ? item.sum : 0)}{" "}
                          </span>
                        </React.Fragment>
                      ))}
                      {dataObject.terminals.length > 1 && (
                        <>
                          <span>
                            <Trans>Total in store</Trans>
                          </span>
                          :
                          <span className={"text-right"}>
                            {dataObject.cash_balance
                              ? numberCurrencyFormat(dataObject.cash_balance)
                              : numberCurrencyFormat(0)}
                          </span>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="flex ">
                      <MinusSmIcon className="h-5 w-5 text-gray-400" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 grid gap-x-8 grid-cols-1 sm:grid-cols-2 sm:border sm:rounded-md sm:px-4">
            <div className="flex flex-wrap gap-x-4 py-2 border-b border-t sm:py-4 sm:border-0">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <AnnotationIcon className="h-5 w-5 text-gray-500" />
                <Trans>Description:</Trans>
              </div>
              <div className="text-gray-90 text-left break-all">
                {dataObject.description ? (
                  dataObject.description
                ) : (
                  <div className="flex ">
                    <MinusSmIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-4 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <Button
              type="button"
              secondary={true}
              label={<Trans>Close</Trans>}
              onClick={() => setOpenPopup(false)}
            />
          </div>
        </>
      ) : (
        <Alert type="error" title={<Trans>Have no data.</Trans>} />
      )}
    </>
  );
};
